import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Eye, 
  Edit, 
  User,
  Calendar,
  MapPin,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { mobileClasses, isMobile, formatForMobile } from '@/utils/mobileUtils';
import { getComplaintPlotLabel } from '@/utils/complaintUtils';

interface Complaint {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'on_hold' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: {
    id: string;
    name: string;
  };
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    block_number?: string;
    flat_number?: string;
  };
  department?: {
    id: string;
    name: string;
  };
  assigned_to?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  location?: string;
  block_number?: string;
  flat_number?: string;
  created_at: string;
  updated_at: string;
  resolution_notes?: string;
}

interface MobileComplaintsTableProps {
  complaints: Complaint[];
  isLoading: boolean;
  onStatusChange?: (complaintId: string, newStatus: string) => void;
  onViewDetails?: (complaint: Complaint) => void;
  isAdmin?: boolean;
}

const MobileComplaintsTable: React.FC<MobileComplaintsTableProps> = ({
  complaints,
  isLoading,
  onStatusChange,
  onViewDetails,
  isAdmin = false
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'assigned': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-purple-100 text-purple-800';
      case 'on_hold': return 'bg-orange-100 text-orange-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'assigned': return <AlertCircle className="h-4 w-4" />;
      case 'in_progress': return <AlertCircle className="h-4 w-4" />;
      case 'on_hold': return <Clock className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      case 'closed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (complaints.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className={`mt-2 ${mobileClasses.textMobile} font-medium text-gray-900`}>No complaints found</h3>
        <p className={`mt-1 ${mobileClasses.textMobile} text-gray-500`}>
          Get started by creating a new complaint.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {complaints.map((complaint) => {
        const plotLabel = getComplaintPlotLabel(complaint);
        return (
        <div key={complaint.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className={`${mobileClasses.cardMobileSmall} border-b border-gray-100`}>
            <div className={`${mobileClasses.flexMobile} justify-between items-start`}>
              <div className="flex-1 min-w-0">
                <h3 className={`${mobileClasses.textMobileLarge} font-medium text-gray-900 truncate`}>
                  {complaint.title}
                </h3>
                <p className={`${mobileClasses.textMobile} text-gray-500 mt-1`}>
                  #{complaint.id.slice(-8)}
                </p>
              </div>
              <div className={`flex ${isMobile() ? 'flex-col gap-1' : 'space-x-2'} ${isMobile() ? 'ml-2' : ''}`}>
                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(complaint.status)}`}>
                  {getStatusIcon(complaint.status)}
                  <span className="ml-1">{complaint.status.replace('_', ' ').toUpperCase()}</span>
                </span>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(complaint.priority)}`}>
                  {complaint.priority.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`${mobileClasses.cardMobileSmall}`}>
            <p className={`${mobileClasses.textMobile} text-gray-600 mb-4`}>
              {formatForMobile.truncateText(complaint.description, 120)}
            </p>

            {/* Details Grid */}
            <div className={`${mobileClasses.grid2} gap-4 mb-4`}>
              <div className="flex items-center text-sm text-gray-500">
                <User className="h-4 w-4 mr-2 text-gray-400" />
                <span className="truncate">
                  {complaint.user.first_name} {complaint.user.last_name}
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                <span>{formatForMobile.formatDate(complaint.created_at)}</span>
              </div>
            </div>

            {plotLabel && (
              <div className="flex items-center text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mb-3">
                <MapPin className="h-4 w-4 mr-2 shrink-0" />
                <span className="font-medium">{plotLabel}</span>
              </div>
            )}
            {complaint.location && (
              <div className="flex items-center text-sm text-gray-500 mb-4">
                <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                <span className="truncate">{complaint.location}</span>
              </div>
            )}

            <div className="flex items-center text-sm text-gray-500 mb-4">
              <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                {complaint.category.name}
              </span>
            </div>

            {/* Actions */}
            <div className={`${mobileClasses.flexMobile} ${isMobile() ? 'space-y-2' : 'space-x-2'} pt-4 border-t border-gray-100`}>
              <button
                onClick={() => onViewDetails?.(complaint)}
                className={`${isMobile() ? 'w-full' : 'flex-1'} inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50`}
              >
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </button>
              <Link
                to={`/app/complaints/${complaint.id}`}
                className={`${isMobile() ? 'w-full' : 'flex-1'} inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700`}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </div>

            {/* Admin Status Change */}
            {isAdmin && onStatusChange && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <label className={`block ${mobileClasses.textMobile} font-medium text-gray-700 mb-2`}>
                  Change Status:
                </label>
                <select
                  value={complaint.status}
                  onChange={(e) => onStatusChange(complaint.id, e.target.value)}
                  className={`${mobileClasses.inputMobile} border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="pending">Pending</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default MobileComplaintsTable;
